"use client";

import { useState } from "react";
import { runSummarizer, AiToolError } from "@/lib/aiTools";
import { inputClass, OutputBox, ErrorBox, FreeTrialNote } from "@/components/aiTools/ToolUI";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import Spinner from "@/components/Spinner";

export default function SummarizerTool() {
  const [input, setInput] = useState("");
  const [length, setLength] = useState<"short" | "medium" | "long">("medium");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);

  async function generate() {
    if (!input.trim()) return;
    setLoading(true);
    setError(null);
    setCapped(false);
    try {
      setOutput(await runSummarizer(input.trim(), length));
    } catch (e) {
      if (e instanceof AiToolError && e.status === 429) {
        setCapped(true);
        setError(e.message);
      } else {
        setError(e instanceof AiToolError ? e.message : "Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Paste the text to summarize</label>
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        rows={6}
        placeholder="Paste an article, email thread, or any long text here…"
        className={inputClass}
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Length:</label>
        <select value={length} onChange={(e) => setLength(e.target.value as typeof length)} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-[#0f172a] dark:text-slate-200">
          <option value="short">Short</option>
          <option value="medium">Medium</option>
          <option value="long">Long</option>
        </select>
        <button
          type="button"
          onClick={generate}
          disabled={loading || !input.trim()}
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-violet-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50"
        >
          {loading ? <Spinner className="h-4 w-4" /> : null}
          {loading ? "Summarizing…" : "Summarize"}
        </button>
      </div>
      <FreeTrialNote />
      {error ? <ErrorBox message={error} /> : null}
      {capped ? (
        <div className="mt-3">
          <UpgradeToPro />
        </div>
      ) : null}
      {output ? <OutputBox output={output} /> : null}
    </div>
  );
}
